import { useNavigate } from "@tanstack/react-router";
import { api, endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import { Album, type AlbumTrack } from "./Album.js";
import { Failed, Loading } from "./Loading.js";
import type { HistoryEvent, RecordingOption } from "./RowMenu.js";
import type { PullPreview, ReleaseOption } from "./TrackListPull.js";

/**
 * The album page, reading one record.
 *
 * Everything the page renders arrives in a single request — the track grid, the "same as rated"
 * candidates, the editions, and whether this is a claimed record with no track list yet. A page
 * that fired five requests would paint in five stages, and this one is a rating surface.
 */
export function AlbumScreen({ albumId, focusSongId }: { albumId: number; focusSongId?: number }) {
  const navigate = useNavigate();
  const album = useResource(() => endpoints.album(albumId), [albumId]);

  if (album.error) return <Failed error={album.error} retry={album.reload} />;
  if (album.loading || !album.data) return <Loading what="the album" />;

  const data = album.data;
  const tracks: AlbumTrack[] = data.tracks.map((t) => ({
    id: t.id,
    no: t.no,
    title: t.title,
    durationMs: t.durationMs,
    isCore: t.isCore,
    rating: t.rating,
    linkedTo: t.linkedTo,
    alsoOn: t.alsoOn,
    sameAs: t.sameAs,
  }));

  return (
    <Album
      // Remount on a different record, so no state leaks between two albums — the cursor, the
      // dismissed trays and the once-only reveal are all per-record.
      key={data.id}
      albumId={data.id}
      focusSongId={focusSongId}
      title={data.title}
      artist={data.artist}
      year={data.year}
      label={data.label}
      genre={data.genre}
      art={data.art}
      tracks={tracks}
      editions={data.editions.map((e) => ({ id: e.id, name: e.name, trackCount: e.trackCount ?? 0 }))}
      cohesion={data.cohesion}
      isTracked={data.isTracked}
      claimed={data.claimed}
      onRate={(songId, value) =>
        void api.put(`/api/songs/${songId}/rating`, {
          value,
          clientEventId: crypto.randomUUID(),
          ratedAt: new Date().toISOString(),
        })
      }
      onCohesion={(cohesion) => void endpoints.cohesion(data.id, cohesion)}
      onEnterBooth={() => void navigate({ to: "/rate" })}
      onToggleCore={(songId, isCore) => void api.put(`/api/songs/${songId}/core`, { isCore })}
      loadHistory={async (songId) => (await api.get<{ events: HistoryEvent[] }>(`/api/songs/${songId}/history`)).events}
      loadRecordings={async (songId) =>
        (await api.get<{ recordings: RecordingOption[] }>(`/api/songs/${songId}/recordings`)).recordings
      }
      searchRecordings={async (q) => (await endpoints.searchRecordings(q)).results}
      onPointAt={(songId, recordingMbid) => void api.put(`/api/songs/${songId}/recording`, { recordingMbid })}
      loadReleases={(id) => api.get<{ releases: ReleaseOption[]; needsChoice: boolean }>(`/api/albums/${id}/releases`)}
      loadPullPreview={(id, releaseMbid) => api.post<PullPreview>(`/api/albums/${id}/track-list/preview`, { releaseMbid })}
      onPull={async (id, releaseMbid) => {
        await api.post(`/api/albums/${id}/track-list`, { releaseMbid });
        // The record just changed shape — new tracks, new core count, and it is tracked now.
        album.reload();
      }}
    />
  );
}
